import {
  ApiNewDokumentPowiazanyDalDto,
  EgibObject,
  FieldId,
  MapObject,
  MapObjectApiType,
  MapState,
} from '@gk/gk-modules';
import { WorkspaceState } from '../../../../../services/request-workspace-state/request-workspace-state.model';
import {
  DocumentationFormType,
  ExtendedApplicantRequest,
  ExtendedApplicantRequestDto,
  ZamowienieDzialkaDto,
} from '../../../../../shared/base-new-request/base-new-request.model';
import { InvestorDetails } from '../../../../../shared/investor-details/investor-details.model';
import { Shipping } from '../../../../../shared/shipping-form/shipping-form.model';
import { PARActType } from '../act-type/act-type.model';
import { PARRequestType } from '../request-type/request-type.model';

export interface PARLetterDto {
  NazwaAktu: string;
  Dzialki: ZamowienieDzialkaDto[];
  CzyTerenObejmujeCaloscDzialek: boolean;
}

export class PARLetter {
  constructor(
    public actName: string,
    public mapState: MapState,
    public isAreaCoversAllParcels: boolean,
  ) {}

  static fromAppToApi(data: PARLetter): PARLetterDto {
    return {
      NazwaAktu: data.actName,
      Dzialki: data.mapState.mapObjectTablesState[0].mapObjects
        .filter(
          <(mapObject: MapObject) => mapObject is EgibObject>(
            ((mapObject) => mapObject.type === MapObjectApiType.LandParcel)
          ),
        )
        .map((parcel) => ZamowienieDzialkaDto.fromAppToApi(parcel)),
      CzyTerenObejmujeCaloscDzialek: data.isAreaCoversAllParcels,
    };
  }
}

export interface PARLetterContentDto extends PARLetterDto {
  Tresc: string;
}

export class PARLetterContent extends PARLetter {
  constructor(
    actName: string,
    mapState: MapState,
    isAreaCoversAllParcels: boolean,
    public content: string,
  ) {
    super(actName, mapState, isAreaCoversAllParcels);
  }

  static override fromAppToApi(data: PARLetterContent): PARLetterContentDto {
    return {
      ...PARLetter.fromAppToApi(data),
      Tresc: data.content,
    };
  }
}

export interface PARLetterDetailsDto extends PARLetterDto {
  KlasaPrzeznaczeniaTerenu: FieldId[];
  MaksymalnyUdzialPowierzchniZabudowy: number;
  MaksymalnaWysokoscZabudowy: number;
  MinimalnyUdzialPowierzchniBiologicznieCzynnej: number;
}

export class PARLetterDetails extends PARLetter {
  constructor(
    actName: string,
    mapState: MapState,
    isAreaCoversAllParcels: boolean,
    public landUseClass: FieldId[],
    public maxBuildingCoverage: number,
    public maxBuildingHeight: number,
    public minBiologicallyActiveArea: number,
  ) {
    super(actName, mapState, isAreaCoversAllParcels);
  }

  static override fromAppToApi(data: PARLetterDetails): PARLetterDetailsDto {
    return {
      ...PARLetter.fromAppToApi(data),
      KlasaPrzeznaczeniaTerenu: data.landUseClass,
      MaksymalnyUdzialPowierzchniZabudowy: data.maxBuildingCoverage,
      MaksymalnaWysokoscZabudowy: data.maxBuildingHeight,
      MinimalnyUdzialPowierzchniBiologicznieCzynnej:
        data.minBiologicallyActiveArea,
    };
  }
}

export interface PARNewRequestDto extends ExtendedApplicantRequestDto {
  OwnerId: FieldId;
  Organ: string;
  CzyWnioskodawcaJestWlascicielem: boolean;
  RodzajAktu: PARActType;
  TrescPisma: string;
  TrescPismaZDzialkami: PARLetterContentDto[];
  SzczegoloweInformacje: PARLetterDetailsDto[];
  RodzajPisma: PARRequestType;
}

export class PARNewRequest extends ExtendedApplicantRequest {
  constructor(
    investorDetails: InvestorDetails,
    documentationForm: DocumentationFormType,
    shippingForm: Shipping,
    proxyDetailsCheckbox: boolean,
    proxyDetailsArray: InvestorDetails[],
    electronicCorrespondenceStatement: boolean,
    public ownerId: FieldId,
    public authority: string,
    public isApplicantOwner: boolean,
    public actType: PARActType,
    public letterContent: string,
    public letterContentWithParcels: PARLetterContent[],
    public letterDetails: PARLetterDetails[],
    public requestType?: PARRequestType,
  ) {
    super(
      investorDetails,
      documentationForm,
      shippingForm,
      electronicCorrespondenceStatement,
      undefined,
      proxyDetailsCheckbox,
      proxyDetailsArray,
    );
  }

  static fromAppToApi(
    data: PARNewRequest,
    files: ApiNewDokumentPowiazanyDalDto[],
  ): PARNewRequestDto {
    return {
      ...ExtendedApplicantRequest.mapCommonFields({
        data,
        files,
      }),
      OwnerId: data.ownerId,
      Organ: data.authority,
      CzyWnioskodawcaJestWlascicielem: data.isApplicantOwner,
      RodzajAktu: data.actType,
      TrescPisma: data.letterContent,
      TrescPismaZDzialkami: data.letterContentWithParcels.map((letterContent) =>
        PARLetterContent.fromAppToApi(letterContent),
      ),
      SzczegoloweInformacje: data.letterDetails.map((letterDetails) =>
        PARLetterDetails.fromAppToApi(letterDetails),
      ),
      RodzajPisma: data.requestType,
    };
  }
}

export interface PARNewRequestFormWorkspaceState extends WorkspaceState {
  formValue: PARNewRequest;
}
